import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Form,
  Input,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Switch,
  Badge,
  Tooltip,
  Select,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  CheckOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  locationService,
  Location,
  LocationCreate,
  LocationUpdate,
} from '../services/locationService';
import { useResponsive } from '../hooks/useResponsive';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import ModernFormModal from '../components/ModernFormModal';
import './Locations.css';

const { Title } = Typography;

const LocationManagement: React.FC = () => {
  const { isMobile } = useResponsive();
  const [locations, setLocations] = useState<Location[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]); // Store all locations for statistics
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [warehouseOnly, setWarehouseOnly] = useState(false);
  const canCreateLocation = RBACService.hasPermission(PERMISSIONS.LOCATION_CREATE);
  const canUpdateLocation = RBACService.hasPermission(PERMISSIONS.LOCATION_UPDATE);
  const canDeleteLocation = RBACService.hasPermission(PERMISSIONS.LOCATION_DELETE);

  // Statistics - always calculate from ALL locations (not filtered)
  const totalLocations = allLocations.length;
  const activeLocations = allLocations.filter((loc) => loc.is_active).length;
  const warehouses = allLocations.filter((loc) => 
    loc.location_type === 'WAREHOUSE' || (loc.is_warehouse && !loc.location_type)
  ).length;

  // Fetch all locations for statistics (no filters)
  const fetchAllLocations = useCallback(async () => {
    try {
      const data = await locationService.getLocations({
        active_only: false, // Get all locations for statistics
        search: undefined,
        is_warehouse: undefined,
        forceRefresh: true,
      });
      setAllLocations(data);
    } catch (error) {
      console.error('Error fetching all locations for statistics:', error);
    }
  }, []);

  // Fetch filtered locations for display
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams: any = {
        search: searchText || undefined,
        is_warehouse: warehouseOnly ? true : undefined, // Filter by warehouse if warehouseOnly is true
        forceRefresh: true,
      };

      if (statusFilter === 'all') {
        baseParams.active_only = false;
      } else if (statusFilter === 'active') {
        // Keep existing default behavior, but be explicit for clarity
        baseParams.is_active = true;
      } else {
        // inactive only
        baseParams.active_only = false;
        baseParams.is_active = false;
      }

      const data = await locationService.getLocations({
        ...baseParams,
      });
      setLocations(data);
      console.log(`Fetched ${data.length} locations (warehouseOnly: ${warehouseOnly}, statusFilter: ${statusFilter})`);
    } catch (error) {
      message.error('Failed to fetch locations');
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchText, warehouseOnly]);

  // Fetch all locations for statistics on mount
  useEffect(() => {
    fetchAllLocations();
  }, [fetchAllLocations]);

  // Fetch filtered locations when filters change
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const showModal = (location?: Location) => {
    if (location) {
      setEditingLocation(location);
      // Ensure location_type is set (fallback to is_warehouse for backward compatibility)
      // Split address into two lines if it contains a comma
      let address_line1 = '';
      let address_line2 = '';
      if (location.address) {
        const addressParts = location.address.split(',').map(s => s.trim());
        if (addressParts.length > 1) {
          address_line1 = addressParts[0];
          address_line2 = addressParts.slice(1).join(', ');
        } else {
          address_line1 = location.address;
        }
      }
      
      const formValues = {
        ...location,
        location_type: location.location_type || (location.is_warehouse ? 'WAREHOUSE' : 'STORE'),
        address_line1,
        address_line2,
      };
      form.setFieldsValue(formValues);
    } else {
      setEditingLocation(null);
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    if (submitting) return;
    setIsModalVisible(false);
    setEditingLocation(null);
    form.resetFields();
  };

  const handleSubmit = async (maybeValues?: any) => {
    if (submitting) return;
    try {
      const values = maybeValues ?? await form.validateFields();
      setSubmitting(true);
      
      // Combine address lines if they exist
      let address = '';
      if (values.address_line1 || values.address_line2) {
        address = [values.address_line1, values.address_line2].filter(Boolean).join(', ');
      } else if (values.address) {
        address = values.address;
      }
      
      // Ensure location_type is set (default to STORE if not provided)
      const formValues = {
        ...values,
        address: address,
        location_type: values.location_type || 'STORE',
      };
      
      // Remove address_line1 and address_line2 from the payload
      delete formValues.address_line1;
      delete formValues.address_line2;
      
      if (editingLocation) {
        // Update location
        await locationService.updateLocation(editingLocation.id, formValues as LocationUpdate);
        message.success('Location updated successfully');
      } else {
        // Create new location
        await locationService.createLocation(formValues as LocationCreate);
        message.success('Location created successfully');
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setEditingLocation(null);
      await fetchLocations();
      await fetchAllLocations();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('Please fill in all required fields');
      } else {
        const detail =
          error?.response?.data?.detail ||
          error?.message ||
          (editingLocation ? 'Failed to update location' : 'Failed to create location');
        message.error(detail);
      }
      console.error('Error submitting location:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await locationService.deactivateLocation(id);
      message.success('Location deactivated successfully');
      fetchLocations();
      fetchAllLocations();
    } catch (error) {
      message.error('Failed to deactivate location');
      console.error('Error deactivating location:', error);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await locationService.updateLocation(id, { is_active: true });
      message.success('Location activated successfully');
      fetchLocations();
      fetchAllLocations();
    } catch (error: any) {
      message.error(error?.response?.data?.detail || 'Failed to activate location');
      console.error('Error activating location:', error);
    }
  };

  const columns: ColumnsType<Location> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 160,
      render: (code: string) => (
        <Tag
          color="blue"
          className="location-code-tag"
        >
          {code}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Location) => {
        return (
          <div
            style={{
              fontWeight: 500,
              maxWidth: isMobile ? 220 : '100%',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}
          >
            {name}
          </div>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'location_type',
      key: 'location_type',
      width: 140,
      render: (location_type: string, record: Location) => {
        // Fallback to is_warehouse for backward compatibility
        const type = location_type || (record.is_warehouse ? 'WAREHOUSE' : 'STORE');
        const colors: { [key: string]: string } = {
          'STORE': 'green',
          'WAREHOUSE': 'orange',
          'GENERAL': 'blue',
          'HOTELS': 'purple',
          'RESTAURANT': 'red',
        };
        const labels: { [key: string]: string } = {
          'STORE': 'Store',
          'WAREHOUSE': 'Warehouse',
          'GENERAL': 'General',
          'HOTELS': 'Hotels',
          'RESTAURANT': 'Restaurant',
        };
        return (
          <Tag color={colors[type] || 'default'}>
            {labels[type] || type}
          </Tag>
        );
      },
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (address?: string) => address || '-',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (is_active: boolean) => (
        <Badge
          status={is_active ? 'success' : 'error'}
          text={is_active ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: any, record: Location) => (
        <Space wrap={false} size={8} style={{ display: 'inline-flex' }}>
          <Tooltip title="View" mouseEnterDelay={0.2}>
            <Button aria-label="View" className="icon-btn icon-btn--info" shape="round" icon={<EyeOutlined />} onClick={() => showModal(record)} />
          </Tooltip>
          {canUpdateLocation && (
            <Tooltip title="Edit" mouseEnterDelay={0.2}>
              <Button aria-label="Edit" className="icon-btn icon-btn--primary" shape="round" icon={<EditOutlined />} onClick={() => showModal(record)} />
            </Tooltip>
          )}
          {canDeleteLocation && !record.is_active && (
            <Popconfirm
              title="Activate this location?"
              onConfirm={() => handleActivate(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Activate" mouseEnterDelay={0.2}>
                <Button aria-label="Activate" className="icon-btn icon-btn--success" shape="round" icon={<CheckOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          {canDeleteLocation && record.is_active && (
            <Popconfirm
              title="Deactivate this location?"
              onConfirm={() => handleDeactivate(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Deactivate" mouseEnterDelay={0.2}>
                <Button aria-label="Deactivate" className="icon-btn icon-btn--danger" shape="round" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="locations-page" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <EnvironmentOutlined /> Location Management
        </Title>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            className="lm-stat-card lm-stat-card--indigo"
            onClick={() => {
              setStatusFilter('all');
              setWarehouseOnly(false);
              setSearchText('');
            }}
          >
            <div className="lm-stat-card__decor" aria-hidden="true" />
            <div className="lm-stat-card__content">
              <div className="lm-stat-card__title">Total Locations</div>
              <div className="lm-stat-card__row">
                <div className="lm-stat-card__iconBox" aria-hidden="true">
                  <EnvironmentOutlined />
                </div>
                <div className="lm-stat-card__value">{totalLocations}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            className="lm-stat-card lm-stat-card--green"
            onClick={() => {
              setStatusFilter('active');
              setWarehouseOnly(false);
              setSearchText('');
            }}
          >
            <div className="lm-stat-card__decor" aria-hidden="true" />
            <div className="lm-stat-card__content">
              <div className="lm-stat-card__title">Active Locations</div>
              <div className="lm-stat-card__row">
                <div className="lm-stat-card__iconBox" aria-hidden="true">
                  <CheckCircleOutlined />
                </div>
                <div className="lm-stat-card__value">{activeLocations}</div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card
            hoverable
            className={`lm-stat-card lm-stat-card--orange ${warehouseOnly ? 'lm-stat-card--selected' : ''}`}
            onClick={() => {
              setWarehouseOnly(true);
              setStatusFilter('all');
              setSearchText('');
            }}
          >
            <div className="lm-stat-card__decor" aria-hidden="true" />
            <div className="lm-stat-card__content">
              <div className="lm-stat-card__title">Warehouses</div>
              <div className="lm-stat-card__row">
                <div className="lm-stat-card__iconBox" aria-hidden="true">
                  <ShopOutlined />
                </div>
                <div className="lm-stat-card__value">{warehouses}</div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table Card - grows to fill remaining viewport */}
      <div className="locations-table-wrapper" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Card 
          className="locations-table-card"
          bodyStyle={{ 
            overflowX: 'auto', 
            overflowY: 'hidden',
            padding: 0,
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
          style={{ 
            position: 'relative',
            zIndex: 1,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        <div style={{ padding: '16px 16px 0 16px', flexShrink: 0 }}>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Input.Search
              className="app-search locations-search"
              placeholder="Search by name or code"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={(value) => setSearchText(value)}
              style={{ width: 300 }}
            />
            <Space>
              <span>Status:</span>
              <Select
                value={statusFilter}
                style={{ width: 160 }}
                className="centered-select-arrow"
                onChange={(value: 'active' | 'inactive' | 'all') => {
                  setStatusFilter(value);
                  if (value === 'active') {
                    setWarehouseOnly(false); // Keep prior behavior: active view is for all stores/warehouses
                  }
                }}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'all', label: 'All' },
                ]}
              />
              {statusFilter === 'inactive' && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Showing inactive locations only
                </span>
              )}
              {statusFilter === 'all' && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Showing active + inactive locations
                </span>
              )}
              {warehouseOnly && (
                <Button
                  size="small"
                  onClick={() => {
                    setWarehouseOnly(false);
                    setStatusFilter('active');
                  }}
                >
                  Clear Warehouse Filter
                </Button>
              )}
            </Space>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchLocations}>
              Refresh
            </Button>
            {canCreateLocation && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
              >
                Add Location
              </Button>
            )}
          </Space>
        </Space>
        </div>

        <div className="locations-table-inner" style={{ position: 'relative', zIndex: 1, flex: 1, minHeight: 0 }}>
        <Table
          columns={columns}
          dataSource={locations}
          rowKey="id"
          loading={loading}
          pagination={{
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} locations`,
            responsive: true
          }}
          scroll={{ 
            x: 1200,
            y: isMobile ? 'max(220px, calc(100dvh - 420px))' : 'max(280px, calc(100dvh - 400px))'
          }}
          size={isMobile ? 'small' : 'middle'}
        />
        </div>
        </Card>
      </div>

      {/* Add/Edit Modal */}
      <ModernFormModal
        open={isModalVisible}
        onClose={handleCancel}
        title={editingLocation ? 'Edit Location' : 'Add New Location'}
        subtitle={editingLocation ? 'Update the location details below.' : 'Fill in the details below to add a new location.'}
        headerVariant="light"
        getContainer={() => (document.querySelector('.main-content-full') as HTMLElement) || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText={editingLocation ? 'Update Location' : 'Create Location'}
        okIcon={<CheckOutlined />}
        onOk={() => form.submit()}
        isSubmitting={submitting}
        destroyOnClose
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="add-location-form"
            initialValues={{
              location_type: 'STORE',
              is_active: true,
            }}
            autoComplete="off"
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Location Name"
                  rules={[{ required: true, message: 'Please enter location name' }]}
                >
                  <Input placeholder="Enter location name" size="large" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="code"
                  label="Location Code"
                  rules={[
                    { required: true, message: 'Please enter location code' },
                    { pattern: /^[A-Z0-9_-]+$/, message: 'Use uppercase letters, numbers, hyphens, and underscores only' },
                  ]}
                >
                  <Input placeholder="e.g., NYC-001" maxLength={20} size="large" autoComplete="off" />
                </Form.Item>
                <span className="add-location-helper-text">Unique identifier for this location</span>
              </Col>
            </Row>

            <Form.Item
              label="Address"
            >
              <Row gutter={16} className="add-location-address-row">
                <Col span={24}>
                  <Form.Item
                    name="address_line1"
                    noStyle
                  >
                    <Input placeholder="Street address, building number" size="large" style={{ marginBottom: 16 }} autoComplete="off" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="address_line2"
                    noStyle
                  >
                    <Input placeholder="City, State, ZIP Code" size="large" autoComplete="off" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>

            <Form.Item
              name="location_type"
              label="Location Type"
              rules={[{ required: true, message: 'Please select location type' }]}
              initialValue="STORE"
            >
              <Select placeholder="Select location type" size="large">
                <Select.Option value="STORE">Store</Select.Option>
                <Select.Option value="WAREHOUSE">Warehouse</Select.Option>
                <Select.Option value="GENERAL">General</Select.Option>
                <Select.Option value="HOTELS">Hotels</Select.Option>
                <Select.Option value="RESTAURANT">Restaurant</Select.Option>
              </Select>
            </Form.Item>

            {editingLocation && (
              <Form.Item
                name="is_active"
                label="Status"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Form.Item>
            )}
          </Form>
        </div>
      </ModernFormModal>
    </div>
  );
};

export default LocationManagement;
